import {
  Component,
  computed,
  ElementRef,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilterBarComponent } from '../../components/filter-bar/filter-bar.component';
import { EventModel, EventType } from '../../models/event.interface';
import { ApiService } from '../../services/api.service';
import { HttpResponse } from '@angular/common/http';
import { PaginationResponse } from '../../models';
import { SessionService } from '../../services/session.service';
import { EventsListComponent } from '../../components/events-list/events-list.component';
import { FeaturedEventsCarouselComponent } from '../../components/featured-events-carousel/featured-events-carousel.component';

const ACTIVE_PAGE_SIZE = 100;
const COMING_SOON_PAGE_SIZE = 30;
const PAST_PAGE_SIZE = 24;

@Component({
  selector: 'hypenation-tickets-home-page',
  standalone: true,
  imports: [
    CommonModule,
    FilterBarComponent,
    EventsListComponent,
    FeaturedEventsCarouselComponent,
  ],
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.css',
})
export class HomePageComponent implements OnInit {
  public totalItems: WritableSignal<number> = signal<number>(0);
  public pastEventsTotalItems: WritableSignal<number> = signal(0);
  public comingSoonTotalItems: WritableSignal<number> = signal(0);

  public page: WritableSignal<number> = signal<number>(0);
  public pastEventsPage: WritableSignal<number> = signal(0);
  public comingSoonPage: WritableSignal<number> = signal(0);

  public events: WritableSignal<Array<EventModel>> = signal<Array<EventModel>>(
    []
  );
  public pastEvents: WritableSignal<EventModel[]> = signal<EventModel[]>([]);
  public comingSoonEvents: WritableSignal<EventModel[]> = signal<EventModel[]>(
    []
  );
  public featuredEvents: WritableSignal<EventModel[]> = signal<EventModel[]>(
    []
  );
  public eventsTypes: WritableSignal<EventType[]> = signal<EventType[]>([]);

  public eventsLoading: WritableSignal<boolean> = signal<boolean>(false);
  public pastEventsLoading: WritableSignal<boolean> = signal<boolean>(false);
  public comingSoonLoading: WritableSignal<boolean> = signal<boolean>(false);
  public featuredEventsLoading: WritableSignal<boolean> =
    signal<boolean>(false);

  public loadingMore: WritableSignal<boolean> = signal<boolean>(false);
  public comingSoonLoadingMore: WritableSignal<boolean> =
    signal<boolean>(false);
  public pastEventsLoadingMore: WritableSignal<boolean> =
    signal<boolean>(false);

  public lastSearchFiltered: WritableSignal<boolean> = signal<boolean>(false);
  public lastCategoryFiltered: WritableSignal<boolean> = signal<boolean>(false);
  public currentSearchTerm: WritableSignal<string> = signal<string>('');
  public currentCategoryName: WritableSignal<string> = signal<string>('');

  public readonly hasMore: Signal<boolean> = computed(
    () => this.events().length < this.totalItems()
  );
  public readonly comingSoonHasMore: Signal<boolean> = computed(
    () => this.comingSoonEvents().length < this.comingSoonTotalItems()
  );
  public readonly pastEventsHasMore: Signal<boolean> = computed(
    () => this.pastEvents().length < this.pastEventsTotalItems()
  );

  @ViewChild('activeSection') activeSection?: ElementRef<HTMLElement>;

  private requestSeq = 0;

  constructor(
    private readonly _apiService: ApiService,
    private readonly sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.getEvents();
    this.getPastEvents();
    this.getFeaturedEvents();
    this.getComingSoonEvents();
    this.getEventTypes();
    this.checkForSession();
    setTimeout(() => this.scrollToTop(), 0);
  }

  public scrollToTop(): void {
    this._apiService.scrollToTop();
  }

  public scrollActiveSectionIntoView(): void {
    if (typeof window === 'undefined') return;
    setTimeout(() => {
      this.activeSection?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  }

  public checkForSession(): void {
    this.sessionService.getSessionId().subscribe({
      error: (err) => {
        console.error('Error fetching session ID:', err);
      },
    });
  }

  public getEvents(sort?: string): void {
    this.eventsLoading.set(true);
    this.page.set(0);
    const seq = ++this.requestSeq;
    this._apiService
      .getAllActiveEventsPaginated(0, ACTIVE_PAGE_SIZE, sort)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          if (seq !== this.requestSeq) return;
          this.events.set(res.body?.content ?? []);
          this.totalItems.set(res.body?.totalElements ?? 0);
          this.eventsLoading.set(false);
        },
        error: () => {
          if (seq !== this.requestSeq) return;
          this.eventsLoading.set(false);
        },
      });
  }

  public loadMoreActive(): void {
    if (!this.hasMore() || this.loadingMore()) return;
    const nextPage = this.page() + 1;
    this.loadingMore.set(true);
    const seq = this.requestSeq;
    const obs = this.lastSearchFiltered()
      ? this._apiService.getAllActiveEventsFilteredPaginated(
          nextPage,
          ACTIVE_PAGE_SIZE,
          this.currentSearchTerm()
        )
      : this.lastCategoryFiltered()
      ? this._apiService.getAllActiveEventsByCategoryPaginated(
          nextPage,
          ACTIVE_PAGE_SIZE,
          this.currentCategoryName()
        )
      : this._apiService.getAllActiveEventsPaginated(
          nextPage,
          ACTIVE_PAGE_SIZE
        );
    obs.subscribe({
      next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
        if (seq !== this.requestSeq) {
          this.loadingMore.set(false);
          return;
        }
        const incoming = res.body?.content ?? [];
        this.events.update((curr) => dedupeAppend(curr, incoming));
        this.totalItems.set(res.body?.totalElements ?? this.totalItems());
        this.page.set(nextPage);
        this.loadingMore.set(false);
      },
      error: () => this.loadingMore.set(false),
    });
  }

  public getPastEvents(sort?: string): void {
    this.pastEventsLoading.set(true);
    this.pastEventsPage.set(0);
    this._apiService
      .getAllPastEventsPaginated(0, PAST_PAGE_SIZE, sort)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          this.pastEvents.set(res.body?.content ?? []);
          this.pastEventsTotalItems.set(res.body?.totalElements ?? 0);
          this.pastEventsLoading.set(false);
        },
        error: () => this.pastEventsLoading.set(false),
      });
  }

  public loadMorePastEvents(): void {
    if (!this.pastEventsHasMore() || this.pastEventsLoadingMore()) return;
    const nextPage = this.pastEventsPage() + 1;
    this.pastEventsLoadingMore.set(true);
    this._apiService
      .getAllPastEventsPaginated(nextPage, PAST_PAGE_SIZE)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          const incoming = res.body?.content ?? [];
          this.pastEvents.update((curr) => dedupeAppend(curr, incoming));
          this.pastEventsTotalItems.set(
            res.body?.totalElements ?? this.pastEventsTotalItems()
          );
          this.pastEventsPage.set(nextPage);
          this.pastEventsLoadingMore.set(false);
        },
        error: () => this.pastEventsLoadingMore.set(false),
      });
  }

  public applyFilters(searchString: string): void {
    if (searchString === '') {
      this.lastSearchFiltered.set(false);
      this.currentSearchTerm.set('');
      this.getEvents();
      this.scrollActiveSectionIntoView();
      return;
    }
    this.eventsLoading.set(true);
    this.lastSearchFiltered.set(true);
    this.lastCategoryFiltered.set(false);
    this.currentSearchTerm.set(searchString);
    this.page.set(0);
    const seq = ++this.requestSeq;
    this._apiService
      .getAllActiveEventsFilteredPaginated(
        0,
        ACTIVE_PAGE_SIZE,
        searchString
      )
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          if (seq !== this.requestSeq) return;
          this.events.set(res.body?.content ?? []);
          this.totalItems.set(res.body?.totalElements ?? 0);
          this.eventsLoading.set(false);
          this.scrollActiveSectionIntoView();
        },
        error: () => {
          if (seq !== this.requestSeq) return;
          this.eventsLoading.set(false);
        },
      });
  }

  public filterByCategory(searchString: string): void {
    this.eventsLoading.set(true);
    this.lastCategoryFiltered.set(true);
    this.lastSearchFiltered.set(false);
    this.currentCategoryName.set(searchString);
    this.page.set(0);
    const seq = ++this.requestSeq;
    this._apiService
      .getAllActiveEventsByCategoryPaginated(0, ACTIVE_PAGE_SIZE, searchString)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          if (seq !== this.requestSeq) return;
          this.events.set(res.body?.content ?? []);
          this.totalItems.set(res.body?.totalElements ?? 0);
          this.eventsLoading.set(false);
          this.scrollActiveSectionIntoView();
        },
        error: () => {
          if (seq !== this.requestSeq) return;
          this.eventsLoading.set(false);
        },
      });
  }

  public clearCategoryFilter(): void {
    this.lastCategoryFiltered.set(false);
    this.lastSearchFiltered.set(false);
    this.currentCategoryName.set('');
    this.currentSearchTerm.set('');
    this.getEvents();
    this.scrollActiveSectionIntoView();
  }

  public getEventTypes(): void {
    this._apiService.getAllEventTypes().subscribe({
      next: (res: HttpResponse<EventType[]>) => {
        this.eventsTypes.set(res.body ?? []);
      },
      error: () => {
        // ignore
      },
    });
  }

  public getComingSoonEvents(): void {
    this.comingSoonLoading.set(true);
    this.comingSoonPage.set(0);
    this._apiService
      .getComingSoonEvents(0, COMING_SOON_PAGE_SIZE)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          this.comingSoonEvents.set(res.body?.content ?? []);
          this.comingSoonTotalItems.set(res.body?.totalElements ?? 0);
          this.comingSoonLoading.set(false);
        },
        error: () => this.comingSoonLoading.set(false),
      });
  }

  public loadMoreComingSoon(): void {
    if (!this.comingSoonHasMore() || this.comingSoonLoadingMore()) return;
    const nextPage = this.comingSoonPage() + 1;
    this.comingSoonLoadingMore.set(true);
    this._apiService
      .getComingSoonEvents(nextPage, COMING_SOON_PAGE_SIZE)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          const incoming = res.body?.content ?? [];
          this.comingSoonEvents.update((curr) => dedupeAppend(curr, incoming));
          this.comingSoonTotalItems.set(
            res.body?.totalElements ?? this.comingSoonTotalItems()
          );
          this.comingSoonPage.set(nextPage);
          this.comingSoonLoadingMore.set(false);
        },
        error: () => this.comingSoonLoadingMore.set(false),
      });
  }

  public getFeaturedEvents(): void {
    this.featuredEventsLoading.set(true);
    this._apiService.getFeaturedEvents(0, 100).subscribe({
      next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
        this.featuredEvents.set(res.body?.content ?? []);
        this.featuredEventsLoading.set(false);
      },
      error: () => this.featuredEventsLoading.set(false),
    });
  }
}

function dedupeAppend(
  curr: EventModel[],
  incoming: EventModel[]
): EventModel[] {
  if (incoming.length === 0) return curr;
  const seen = new Set(curr.map((e) => e.uid));
  const filtered = incoming.filter((e) => !seen.has(e.uid));
  return filtered.length === 0 ? curr : [...curr, ...filtered];
}
